import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { WebApiService } from 'starbase-login';
import { Observable, of, range, throwError, forkJoin } from 'rxjs';
import { ImageType } from '../models/imageType';
import { retry, catchError, tap, switchMap, map } from 'rxjs/operators';
import { TypeaheadResults } from '../models/typeaheadResults';
import { RelatedSetLinkingModel } from '../models/relatedSetLinkingModel';
import { ImageIndividual } from '../models/imageIndividual';
import { BoundImageSet } from '../models/boundImageSet';
import { CallNumberModel } from '../models/callNumberModel';
import { StarbaseDataServiceWithRequisites, FilterParams } from 'starbase-launcher';
import { QMManual } from '../models/qmmanual';
import { IRowModel } from 'table-editor';
import { ImageFile } from '../models/imageFile';
import { RelatedSetModel, RelatedSetTypeEnum } from '../models/relatedSetModel';
import { RelatedImageSetViewDataModel } from '../models/relatedImageSetViewDataModel';

@Injectable({
  providedIn: 'root'
})
export class LookupDataService extends StarbaseDataServiceWithRequisites<ImageType> {

//#region URL declarations

  public readonly imageTypesUrl = '/api/TypeOfImageModels'; // GET all

  public readonly putImageTypeUrl = '/api/TypeOfImageModels/'; // PUT one

  public readonly postImageTypeUrl = '/api/TypeOfImageModels'; // POST one

  public readonly deleteImageTypeUrl = '/api/TypeOfImageModels/'; // DEL one

  // Quartermaster Specification Category
  public readonly getLUSpecificationCategoryUrl = '/api/LU_QMSpecificationCategoryModel';

  public readonly postLUSpecificationCategoryUrl = '/api/LU_QMSpecificationCategoryModel';

  public readonly deleteLUSpecificationCategoryUrl = '/api/LU_QMSpecificationCategoryModel/';

  public readonly putLUSpecificationCategoryUrl = '/api/LU_QMSpecificationCategoryModel/';

  // Quartermaster Models
  public readonly QuartermasterURL = '/api/QM_ManualModel';
  public readonly quartermasterAll = '/api/QM_ManualModel/GetAllQMsRaw/';
  public readonly getQMLinksUrl = '/api/QM_ManualModel/GetQMManualModelsByImage?imageId=';
  public readonly QM_RelatedImagesURL = '/api/QM_ManualModel/GetRelatedImages';

  public readonly nextBatchNoUrl = '/api/BatchLogModels/GetNextBatchNo'; // GET one

  public readonly getBatchLogUrl = '/api/BatchLogModels'; // GET one

  public readonly postBatchLogObjectUrl = '/api/BatchLogModels'; // POST one

  public readonly putBatchLogUrl = '/api/BatchLogModels/'; // PUT one

  public readonly renameBatchLogSetUrl = '/api/BatchLogModels/RenameBatchLogModel'; // PUT one (rename)

  public readonly deleteBatchLogUrl = '/api/BatchLogModels/'; // DEL one

  public readonly typeaheadControllerUrl = '/api/Typeahead/TypeaheadSearch'; // Search by multiple entities, requesting rpp (results per page)

  public readonly getStatesUrl = '/api/StateModels'; // GET all

  public readonly postStateUrl = '/api/StateModels'; // POST one

  public readonly putStateUrl = '/api/StateModels/'; // PUT one

  public readonly deleteStateUrl = '/api/StateModels/'; // DEL one

  public readonly deleteKeywordUrl = '/api/KeywordModels/'; // DEL one

  public readonly postKeywordUrl = '/api/KeywordModels'; // POST one

  public readonly deleteImageDateUrl = '/api/ImageDatesModels/'; // DEL one

  public readonly getImageDatesByRecordUrl = '/api/ImageDatesModels/GetImageDatesByImageRecord?imageId='; // GET many

  public readonly deleteUnitLinkUrl = '/api/UnitLinkingModels/'; // DEL one

  public readonly getUnitLinksUrl = '/api/UnitLinkingModels/GetUnitLinkingModelsByImage?imageId=';

  public readonly getWeaponLinksUrl = '/api/WeaponLinkingModels/GetWeaponLinkingModelsByImage?imageId=';

  public readonly deleteWeaponLinkUrl = '/api/WeaponLinkingModels/'; // DEL one

  public readonly getArmiesUrl = '/api/ArmyModels'; // GET all

  public readonly postArmyUrl = '/api/ArmyModels'; // POST one

  public readonly putArmyUrl = '/api/ArmyModels/'; // // PUT one

  public readonly deleteArmyUrl = '/api/ArmyModels/'; // DEL one

  public readonly getPrimaryWeaponsUrl = '/api/LU_PrimaryWeapon'; // GET all

  public readonly putPrimaryWeaponUrl = '/api/LU_PrimaryWeapon/'; // PUT one

  public readonly postPrimaryWeaponUrl = '/api/LU_PrimaryWeapon'; // POST one

  public readonly deletePrimaryWeaponUrl = '/api/LU_PrimaryWeapon/'; // DEL one

  public readonly getWeaponsUrl = '/api/weaponModels'; // GET all

  public readonly postWeaponUrl = '/api/weaponModels'; // POST one

  public readonly putWeaponUrl = '/api/weaponModels/'; // PUT one

  public readonly deleteWeaponUrl = '/api/weaponModels/'; // DEL one

  public readonly getRanksUrl = '/api/rankModels'; // GET all

  public readonly postRankUrl = '/api/rankModels'; // POST one

  public readonly putRankUrl = '/api/rankModels/'; // PUT one

  public readonly deleteRankUrl = '/api/rankModels/'; // DEL one

  public readonly getRankCatsUrl = '/api/rankCategoryModels'; // GET all

  public readonly postRankCatUrl = '/api/rankCategoryModels'; // POST one

  public readonly putRankCatUrl = '/api/rankCategoryModels/'; // PUT one

  public readonly deleteRankCatUrl = '/api/rankCategoryModels/'; // DEL one

  public readonly getLUArmiesUrl = '/api/LU_army'; // GET all

  public readonly postLUArmyUrl = '/api/LU_army'; // POST one

  public readonly putLUArmyUrl = '/api/LU_army/'; // PUT one

  public readonly deleteLUArmyUrl = '/api/LU_army/'; // DEL one

  public readonly getBattlesUrl = '/api/Battle'; // GET all

  public readonly getBattleUrl = '/api/Battle/'; // GET one

  public readonly postBattleUrl = '/api/Battle'; // POST one

  public readonly putBattleUrl = '/api/Battle/'; // PUT one

  public readonly deleteBattleUrl = '/api/Battle/'; // DEL one

  public readonly getRegimentNosUrl = '/api/RegimentNoModels'; // GET all

  public readonly postRegimentNoUrl = '/api/RegimentNoModels'; // POST one

  public readonly putRegimentNoUrl = '/api/RegimentNoModels/'; // PUT one

  public readonly deleteRegimentNoUrl = '/api/RegimentNoModels/'; // DEL one

  public readonly getBranchesUrl = '/api/BranchOfServiceModels'; // GET all

  public readonly postBranchUrl = '/api/BranchOfServiceModels'; // POST one

  public readonly putBranchUrl = '/api/BranchOfServiceModels/'; // PUT one

  public readonly deleteBranchUrl = '/api/BranchOfServiceModels/'; // DEL one

  public readonly deleteImageIndividualUrl = '/api/ImageIndividualModels/'; // DEL one

  public readonly getImageIndividualsByImageUrl = '/api/ImageIndividualModels/GetImageIndividualModelsByImage?imageId='; // GET many

  // Quartermaster Source Reference Mode
  public readonly getLUQmSourceReferenceUrl = '/api/LU_QMSourceReferenceModel';

  public readonly postLUQmSourceReferenceUrl = '/api/LU_QMSourceReferenceModel';

  public readonly deleteLUQmSourceReferenceUrl = '/api/LU_QMSourceReferenceModel/';

  public readonly putLUQmSourceReferenceUrl = '/api/LU_QMSourceReferenceModel/';

  public readonly getLUSourcesUrl = '/api/LU_SourceModels'; // GET all

  public readonly getLUSourceUrl = '/api/LU_SourceModels/'; // GET one

  public readonly postLUSourceUrl = '/api/LU_SourceModels'; // POST one

  public readonly deleteLUSourceUrl = '/api/LU_SourceModels/'; // DEL one

  public readonly putLUSourceUrl = '/api/LU_SourceModels/'; // PUT one

  public readonly getCallNumberBySourceUrl = '/api/CallNumberModels/BySource?id='; // GET many

  public readonly getCallNumbersUrl = '/api/CallNumberModels'; // GET many

  public readonly postCallNumberUrl = '/api/CallNumberModels'; // POST one

  public readonly putCallNumberUrl = '/api/CallNumberModels/'; // PUT one

  public readonly delCallNumberUrl = '/api/CallNumberModels/'; // DEL one

  public readonly postRelatedSetUrl = '/api/RelatedImageSetModels'; // POST one

  public readonly getExistingSetsForMultipleImagesUrl = '/api/RelatedImageSetModels/GetExistingSetsForImageIds'; // GET many

  public readonly getExistingSetsForOneImageUrl = '/api/RelatedImageSetModels/GetExistingSetsForOneImage?imageId='; // GET one

  public readonly getImagesByRelatedSetUrl = '/api/RelatedImageSetModels/GetImagesByRelatedSetUID';

  public readonly getRecordSetsByNameUrl = '/api/RelatedImageSetModels/GetAllRecordsOfRelatedSetByName'; // GET many

  public readonly getAllRecordSetsUrl = '/api/RelatedImageSetModels'; // GET all
  
  public readonly getAllRelatedSetsUrl = '/api/RelatedImageSetModels/GetAllRelatedSets'; // GET all related Sets

  public readonly processRelatedSetBatchUrl = '/api/RelatedImageSetModels/ProcessBatch';

  public readonly putRecordSetsUrl = '/api/RelatedImageSetModels/'; // PUT one

  public readonly deleteRelatedImageSetModelUrl = '/api/RelatedImageSetModels/DeleteRelatedSetModel/'; // DEL one

  public readonly deleteFullRelatedSetUrl = '/api/RelatedImageSetModels/DeleteFullRelatedSetModel/'; // DEL all

  public readonly getApiVersionDataUrl = '/api/info'; // GET static data

  public readonly getAllImageIndividualsUrl = '/api/ImageIndividualModels'; // GET all

  public readonly getAllImageIndividualsNames = '/api/ImageIndividualModels/GetImageIndividualsNames'; // GET some properties of all records

  public readonly postBoundSetUrl = '/api/BoundImageSetModels'; // POST one

  public readonly getExistingBoundSetsForOneImageUrl = '/api/BoundImageSetModels/GetExistingSetsForOneImage?imageId='; // GET one

  public readonly getBoundRecordSetsByNameUrl = '/api/BoundImageSetModels/GetRecordsOfSetByName'; // GET many

  public readonly getAllBoundRecordSetsByNameUrl = '/api/BoundImageSetModels/GetAllRecordsOfSetByName'; // GET all

  public readonly getAllBoundRecordSetsUrl = '/api/BoundImageSetModels'; // GET all

  public readonly putBoundRecordSetsUrl = '/api/BoundImageSetModels/renameboundimageset'; // PUT one

  public readonly deleteBoundImageSetUrl = '/api/BoundImageSetModels/DeleteBoundImageSetModel'; // DEL one

  public readonly deleteEntireBoundImageSetUrl = '/api/BoundImageSetModels/DeleteEntireBoundImageSet'; // delete entire set
//#endregion URL declarations

  protected _staleDataTimeout = 1000; // ms

  protected _getOneUri: string;
  protected _getAllUri: string;
  protected _putUri: string;
  protected _postUri: string;
  protected _deleteUri: string;
  protected _broadSearchUri = '';

    /* Naive Individuals CACHE */
  private imageIndividualsCache: ImageIndividual[] = [];

  constructor(private _http: HttpClient, private _webAPI: WebApiService) {
    super(_http, _webAPI);
    this._getAllUri = '';  // this.imageTypesUrl;
    this._getOneUri = '';  // this.imageTypesUrl;
    this._putUri = this.putImageTypeUrl;
    this._postUri = this.postImageTypeUrl;
    this._deleteUri = this.deleteImageTypeUrl;
  }

  public getDataByFilterParamsBroadSearch(url: string, filterParams: FilterParams<ImageType>): Observable<FilterParams<ImageType>> {
    throw new Error('Method not implemented.');
  }

  public getExistingSetForOneImage(id: string): Observable<RelatedSetLinkingModel[]> {
    return this._http.get<RelatedSetLinkingModel[]>(
      this.server + this.getExistingSetsForOneImageUrl + '?IdArray=' + id,
      { headers: this._webAPI.getApiHeaders('get') })
      .pipe(retry(1), catchError(e => this._webAPI.handleRequestError(e)));
  }

  public deleteRelatedImageSet(obj: IRowModel) { // uses POST instead of DELETE
    return this.postRequisiteData(this.deleteRelatedImageSetModelUrl, obj);
  }

  public deleteFullRelatedSet(obj: IRowModel) { // uses POST instead of DELETE
    return this.postRequisiteData(this.deleteFullRelatedSetUrl, obj);
  }

  public getAllQMManuals() {
    return this.getAllRequisiteData<QMManual>(this.quartermasterAll);
  }

  public deleteImageTypeFromServer(id: string | number): Observable<any> {
    return this.deleteData(this.deleteImageTypeUrl, id);
  }

  public deleteLUQMSourceReference(id: string | number): Observable<any> {
    return this.deleteData(this.deleteLUQmSourceReferenceUrl, id);
  }

  public deleteLUArmyData(id: string | number): Observable<any> {
    return this.deleteData(this.deleteLUArmyUrl, id);
  }

  public deleteArmyFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteArmyUrl, id.toString());
  }

  public deleteBattleFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteBattleUrl, id);
  }

  public deleteStateFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteStateUrl, id.toString());
  }

  public deletePrimaryWeaponFromServer(id: number): Observable<any> {
    return this.deleteData(this.deletePrimaryWeaponUrl, id.toString());
  }

  public deleteWeaponFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteWeaponUrl, id.toString());
  }


  public deleteRankFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteRankUrl, id.toString());
  }

  public deleteRankCatFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteRankCatUrl, id.toString());
  }

  public deleteRegimentNo(id: number): Observable<any> {
    return this.deleteData(this.deleteRegimentNoUrl, id.toString());
  }

  public deleteBranchFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteBranchUrl, id.toString());
  }

  public deleteLUSourceFromServer(id: number): Observable<any> {
    return this.deleteData(this.deleteLUSourceUrl, id.toString());
  }


  // eslint-disable-next-line @typescript-eslint/naming-convention
  public deleteLU_QM_SpecificationCategory(id: string): Observable<any> {
    return this.deleteData(this.deleteLUSpecificationCategoryUrl, id);
  }

  public getCallNumberBySourceFromServer(sourceId: number, includeEmptySets: boolean = false): Observable<CallNumberModel[]> {
    return this.getAllRequisiteData<CallNumberModel>(this.getCallNumberBySourceUrl + sourceId + '&includeEmptySets=' + includeEmptySets);
  }
  public getCallNumbersFromServer(includeEmptySets: boolean = false): Observable<any> {
    return this.getAllRequisiteData<CallNumberModel>(
      this.getCallNumbersUrl + '?includeEmptySets=' + includeEmptySets);
  }

  public deleteCallNoFromServer(id: number): Observable<any> {
    return this.deleteData(this.delCallNumberUrl, id.toString());
  }

  public processBatchRelatedSet(payload: any): Observable<any> {
    return this._http.post(
      this.server + '/api/RelatedImageSetModels/ProcessBatch',
      payload,
      { headers: this._webAPI.getApiHeaders('post') }
    ).pipe(
      retry(1),
      catchError(error => this._webAPI.handleRequestError(error))
    );
  }

  public saveNewRelatedImageSetToServer(riset: RelatedSetLinkingModel): Observable<any> {
    // Avoid circular reference
    const payload = {
      name: riset.name,
      relatedSetUID: riset.relatedSetUID,
      imageModelId: riset.imageModelId,
      sequenceNumber: riset.sequenceNumber,
      ordnanceReturnSet: riset.ordnanceReturnSet
    };
  
    return this.postRequisiteData<any>(this.postRelatedSetUrl, payload);
  }

  // save new bound image set
  public saveNewBoundImageSetToServer(biset: BoundImageSet): Observable<any> {
    biset.imageModel = null;
    biset['imageModelUID'] = biset.imageModelId;
    return this.postRequisiteData<BoundImageSet>(this.postBoundSetUrl, biset);
  }

  // get all sets for a single image
  // public getExistingBoundImageSetsForOneImage(imageId: number): Observable<BoundImageSet[]> {
  //   return this._http.get<BoundImageSet[]>(this.server + this.getExistingBoundSetsForOneImageUrl + '?imageId=' + imageId, {
  //     headers: this._webAPI.getApiHeaders('get')
  //   }).pipe(retry(this.defaultRetries), catchError(e => this._webAPI.handleRequestError(e)));
  // }

  public deleteEntireBoundImageSet(name: string) { // does not use traditional StarbaseDataService Delete.
    return this._http.delete(
      this.server + this.deleteEntireBoundImageSetUrl + '?boundSetName=' + name,
      { headers: this._webAPI.getApiHeaders('delete') })
      .pipe(catchError(e => this._webAPI.handleRequestError(e)));
  }
  // get a page of images included in a particular set
  public getBoundImagesSetByNameFromServer(setName: string, page = 1, ascendingSequenceOrder = true): Observable<BoundImageSet[]> {
    return this.getAllRequisiteData<BoundImageSet>(
      `${this.getBoundRecordSetsByNameUrl}?setName=${encodeURIComponent(setName)}&page=${page}&sequenceSortAsc=${ascendingSequenceOrder}`);
  }
  // get all of images included in a particular set
  public getAllBoundImagesSetByNameFromServer(setName: string, ascendingSequenceOrder = true): Observable<BoundImageSet[]> {
    return this.getAllRequisiteData<BoundImageSet>(
      `${this.getAllBoundRecordSetsByNameUrl}?setName=${encodeURIComponent(setName)}&sequenceSortAsc=${ascendingSequenceOrder}`);
  }
  // rename bound set
  public renameBoundImageSet(oldName: string, newName: string): Observable<any> { // does not use traditional StarbaseDataService Put.
    const body = {
      oldName: oldName,
      newName: newName
    };
    return this._http.put(
      this.server + this.putBoundRecordSetsUrl, 
      body,
      { headers: this._webAPI.getApiHeaders('put') }).pipe(catchError(e => this._webAPI.handleRequestError(e)));
  }

  public getBoundImageSetByName(setName: string): Observable<BoundImageSet[]> {
    return this.getAllRequisiteData<BoundImageSet>(this.getBoundRecordSetsByNameUrl + '?setName=' + setName);
  }
  public getBoundImageSetByImage(imgId: number): Observable<BoundImageSet[]> {
    return this.getAllRequisiteData<BoundImageSet>(this.getExistingBoundSetsForOneImageUrl + imgId);
  }

  // delete one image from a set. Uses POST, not DELETE.
  public deleteBoundImageFromSet(data: BoundImageSet) {
    return this.postRequisiteData<BoundImageSet>(this.deleteBoundImageSetUrl, data);
    }
  // save modified sequence number for bound image set
  saveModifiedBoundImageSetSequenceNumber(id: string, set: any) {
    return this._http.put(
      this.server + this.putBoundRecordSetsUrl + '/' + id,
      set,
      { headers: this._webAPI.getApiHeaders('put') }).pipe(catchError(e => this._webAPI.handleRequestError(e)));
  }
  // public getExistingRelatedImageSetsForMultipleImages(imageIds: number[]): Observable<RelatedSetLinkingModel[]> {
  //   // let qry = '?IdArray=';
  //   // for (const i of imageIds) {
  //   //   qry += i + ',';
  //   // }
  //   // qry = qry.slice(0, -1); // remove last comma
  //   return this.postRequisiteData<number[]>(this.getExistingSetsForMultipleImagesUrl, imageIds);
  // }

  public getLedgerSetsForGallery(): Observable<FilterParams<RelatedImageSetViewDataModel>> {
    const filters = new FilterParams<RelatedImageSetViewDataModel>();
    filters.page = 1;
    filters.rpp = 35;
    
    return this._http.post<FilterParams<RelatedImageSetViewDataModel>>(
      this.server + '/api/RelatedImageSetModels/GetLedgerSetsForGallery',
      filters,
      { headers: this._webAPI.getApiHeaders('post') }
    ).pipe(
      retry(1),
      catchError(e => this._webAPI.handleRequestError(e))
    );
  }

  public getLedgerSetsByFilterParams(filters: FilterParams<ImageFile>): Observable<any> {
    return this._http.post<any>(
      this.server + '/api/ImageModels/GetLedgerSetsByFilterParams',
      filters,
      { headers: this._webAPI.getApiHeaders('post') }
    ).pipe(
      retry(1),
      catchError(e => this._webAPI.handleRequestError(e))
    );
  }

  public getExistingRelatedImageSetsForMultipleImages(imageIds: number[]): Observable<RelatedSetModel[]> {
    return this._http.post<RelatedSetModel[]>(
      this.server + this.getExistingSetsForMultipleImagesUrl,
      imageIds,
      { headers: this._webAPI.getApiHeaders('post') }
    ).pipe(retry(1), catchError(e => this._webAPI.handleRequestError(e)));
  }

  public processRelatedSetBatch(payload: any): Observable<any> {
    return this.postRequisiteData(this.processRelatedSetBatchUrl, payload);
  }

  public addImagesToRelatedSet(setUID: number, imageUIDs: number[]) {
    
  }

  public getAllRelatedSets(): Observable<RelatedSetModel[]> {
    return this.getAllRequisiteData<RelatedSetModel>(this.getAllRelatedSetsUrl);
  }

  getRelatedImagesForQMSpec(qmid: number, page = 1, ascendingSequenceOrder = true): Observable<ImageFile[]> {
    return this.getAllRequisiteData<ImageFile>(
      `${this.QM_RelatedImagesURL}?id=${qmid}&page=${page}&sequenceSortAsc=${ascendingSequenceOrder}`);
  }

  public getExistingRelatedImageSetsForOneImage(imageId: number): Observable<RelatedSetModel[]> {
    return this.getAllRequisiteData<RelatedSetModel>(
      this.getExistingSetsForOneImageUrl + imageId
    );
  }

  public getRecordsInSetByNameFromServer(setUID: number, page = 1, ascendingSequenceOrder = true, rpp = 10): Observable<FilterParams<RelatedSetLinkingModel>> {
    const filters = new FilterParams<RelatedSetLinkingModel>();
    filters.filters = [['RelatedSetUID', setUID.toString()]];
    filters.page = page;
    filters.sortAsc = ascendingSequenceOrder;
    filters.sortFields = ['SequenceNumber'];
    
    return this.postRequisiteData<FilterParams<RelatedSetLinkingModel>>(
      this.getRecordSetsByNameUrl,
      filters
    );
  }

  public saveModifiedRecordSetToServer(set: RelatedSetModel): Observable<any> {
    return this.putRequisiteData(this.putRecordSetsUrl, set.uid, set);
  }

  public getAllImageIndividualsFromServer(): Observable<ImageIndividual[]> {
    if (this.imageIndividualsCache.length > 0) {
      // eslint-disable-next-line no-console
      console.info('returning cached ImgInd data');
      return of(this.imageIndividualsCache);
    } else {
      return this._http.get<ImageIndividual[]>(this.server + this.getAllImageIndividualsNames,
        { headers: this._webAPI.getApiHeaders('get') })
        .pipe(
          retry(1),
          tap(x => this.imageIndividualsCache = x),
          catchError(e => this._webAPI.handleRequestError(e)));
    }
  }

  // Hardcoded A–P, excluding J for Company Letters values.
  public getAllCompanyLetters(): Observable<string[]> {
    return of([
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M', 'N', 'O', 'P'
    ]);
  }

  // Hardcoded 1–30 for Corps values
  public getAllCorps(): Observable<number> {
    return range(1, 30);
  }

  /* CACHE METHODS */

  public clearImageIndividualsCache(): void {
    this.imageIndividualsCache = [];
  }

// #region TYPEAHEAD CALLS */

  /** Calls to the API server to run a Typeahead search,
   *  using Starbase BroadSearch. Only executes if the provided term text is longer than 1 char.
   *
   * @param term The user text to search by. If this is less than 2 in length, nothing will be sent to the server.
   * @param entities The list of model entities to search through.
   * @param rpp Optional. The number of results to return per entity match collection. Defaults to 5.
   * @param imageTypeUID Optional. If provided, it should be the value of the ImageType's UID to filter by.
   * This only affects certain entities.
   * @param explicitEntityProps Optional. If provided, it should represent a collection of properties to search,
   *  existing on one of the entities declared in `entities`
   * and must match the name of the property on the API
   *  server-side counterpart class. The counterpart property must have [SearchableField] attribute. See API docs for more info.
   * @returns The resulting `TypeaheadResults` object from the server,
   *  which will contain the hits found or any errors encountered during execution.
   */
  public typeaheadSearch(term: string, entities: string[],
    rpp: number = 5,
    imageTypeUID?: number,
    explicitEntityProps?: string[]): Observable<TypeaheadResults> {
    if (term.trim().length < 2 || !entities.length) {
      return of(new TypeaheadResults(term, entities));
    }
    return this._http.get<TypeaheadResults>(
      // eslint-disable-next-line max-len
      `${this.server}${this.typeaheadControllerUrl}?entities=${entities.join(',')}&searchPhrase=${term.trim()}&rpp=${rpp}${imageTypeUID ? '&imageTypeId=' + imageTypeUID : ''}${explicitEntityProps ? '&explicitEntityProperties=' + explicitEntityProps.join(',') : ''}`,
      { headers: this._webAPI.getApiHeaders('get') }
    ).pipe(
      tap(a => a.matches.forEach(m => m.selected = false)),
      catchError(e => this._webAPI.handleRequestError(e))
    );
  }

// #endregion END TYPEAHEAD CALLS */

  public renameBatchLogSet(newName: string, id: number): Observable<any> {
    return this.putRequisiteData(this.renameBatchLogSetUrl + '?id=' + id + '&newName=' + newName, '', null);
  }

  public getAllRelatedSetLinks(): Observable<RelatedSetLinkingModel[]>{
    return this.getAllRequisiteData<RelatedSetLinkingModel>(this.getAllRelatedSetsUrl);
  }

}
